import { createApi } from '@reduxjs/toolkit/query/react';
import type { FilterDTOType } from '../../../../schemas';
import {
  type ContentUpdateRequestType,
  type LookDTOType,
  type LookGroupDTOType,
  type LookGroupLookDTOType,
  type LookGroupListResponseType,
  LookGroupDTOSchema,
  LookGroupListResponseSchema,
  LookGroupLookDTOSchema,
  type LookListResponseType,
  type QCRejectionDTOType,
} from '../../../../schemas/styling';
import { stylistBaseQuery, stylistTagTypes } from '../common';

export type LookGroupsQueryArg = {
  occasion?: number;
  sub_occasion?: number;
  is_pinned?: boolean;
  enrich_looks?: boolean;
};

export type LookGroupsListQueryArg = LookGroupsQueryArg & {
  page_size?: number;
};

export const stylistLooksApi = createApi({
  reducerPath: 'stylistLooksApi',
  baseQuery: stylistBaseQuery,
  tagTypes: [...stylistTagTypes],
  endpoints: builder => ({
    getLooks: builder.infiniteQuery<
      LookListResponseType,
      {
        workflow_id?: number;
        filters?: Array<FilterDTOType>;
      },
      {
        page: number;
        size: number;
        workflow_id?: number;
        filters: Array<FilterDTOType>;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          page: 1,
          size: 10,
          filters: [],
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage) {
            return undefined;
          }

          if (Array.isArray(lastPage)) {
            if (lastPage.length === 0 || lastPage.length < lastPageParam.size) {
              return undefined;
            }
          } else if (
            lastPage.results.length === 0 ||
            lastPage.results.length < lastPageParam.size
          ) {
            return undefined;
          }

          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
            workflow_id: lastPageParam.workflow_id,
            filters: lastPageParam.filters,
          };
        },
      },
      query: ({ pageParam, queryArg }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('page', pageParam.page.toString());
        searchParams.append('page_size', pageParam.size.toString());

        if (queryArg.workflow_id) {
          searchParams.append('workflow', queryArg.workflow_id.toString());
        }

        (queryArg.filters ?? []).forEach(filter => {
          searchParams.append(filter.field, filter.value);
        });

        return {
          url: `/stylist/looks/?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Look'],
    }),

    getLookById: builder.query<LookDTOType, number>({
      query: id => ({
        url: `/stylist/looks/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Look', id }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedLook } = await queryFulfilled;
          updateLookInLooksCache(dispatch, id, updatedLook);
        } catch (error) {
          console.error('Error updating look in cache:', error);
        }
      },
    }),

    rejectLook: builder.mutation<LookDTOType, QCRejectionDTOType>({
      query: ({ look_id, ...body }) => ({
        url: `/stylist/looks/${look_id}/reject/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Look'],
    }),

    updateLookContent: builder.mutation<
      LookDTOType,
      { id: number; data: ContentUpdateRequestType }
    >({
      query: ({ id, data }) => ({
        url: `/stylist/looks/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedLook } = await queryFulfilled;
          updateLookInRelatedCaches(dispatch, id, updatedLook);
        } catch (error) {
          console.error('Error updating look content cache:', error);
        }
      },
    }),

    publishLook: builder.mutation<LookDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/stylist/looks/${id}/publish/`,
        method: 'POST',
      }),
      invalidatesTags: ['Look'],
    }),

    unpublishLook: builder.mutation<LookDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/stylist/looks/${id}/unpublish/`,
        method: 'POST',
      }),
      invalidatesTags: ['Look'],
    }),

    generateVisualSearch: builder.mutation<LookDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/stylist/looks/${id}/generate-visual-search/`,
        method: 'POST',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedLook } = await queryFulfilled;
          updateLookInRelatedCaches(dispatch, id, updatedLook);
        } catch (error) {
          console.error('Error updating generate-visual-search cache:', error);
        }
      },
    }),

    reRunLook: builder.mutation<LookDTOType, { id: number }>({
      query: ({ id }) => ({
        url: `/stylist/looks/${id}/re-run/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Look'],
    }),

    getLookGroups: builder.infiniteQuery<
      LookGroupListResponseType,
      LookGroupsQueryArg,
      {
        page: number;
        size: number;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          page: 1,
          size: 1,
        },
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage || lastPage.length === 0 || lastPage.length < lastPageParam.size) {
            return undefined;
          }

          return {
            page: lastPageParam.page + 1,
            size: lastPageParam.size,
          };
        },
      },
      query: ({ pageParam, queryArg }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('page', String(pageParam.page));
        searchParams.append('page_size', String(pageParam.size));

        if (typeof queryArg.occasion === 'number') {
          searchParams.append('occasion', String(queryArg.occasion));
        }

        if (typeof queryArg.sub_occasion === 'number') {
          searchParams.append('sub_occasion', String(queryArg.sub_occasion));
        }

        if (typeof queryArg.is_pinned === 'boolean') {
          searchParams.append('is_pinned', String(queryArg.is_pinned));
        }

        if (queryArg.enrich_looks) {
          searchParams.append('enrich_looks', 'true');
        }

        return {
          url: `/stylist/look-groups/?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      extraOptions: { zodSchema: LookGroupListResponseSchema },
      providesTags: ['LookGroup'],
    }),

    getLookGroupsList: builder.query<LookGroupListResponseType, LookGroupsListQueryArg>({
      query: queryArg => {
        const searchParams = new URLSearchParams();
        searchParams.append('page', '1');
        searchParams.append('page_size', String(queryArg.page_size));

        if (typeof queryArg.occasion === 'number') {
          searchParams.append('occasion', String(queryArg.occasion));
        }

        if (typeof queryArg.sub_occasion === 'number') {
          searchParams.append('sub_occasion', String(queryArg.sub_occasion));
        }

        if (typeof queryArg.is_pinned === 'boolean') {
          searchParams.append('is_pinned', String(queryArg.is_pinned));
        }

        if (queryArg.enrich_looks) {
          searchParams.append('enrich_looks', 'true');
        }

        return {
          url: `/stylist/look-groups/?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['LookGroup'],
      extraOptions: { zodSchema: LookGroupListResponseSchema },
    }),

    getLookGroupById: builder.query<LookGroupDTOType, number>({
      query: id => ({
        url: `/stylist/look-groups/${id}/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'LookGroup', id }],
      extraOptions: {
        zodSchema: LookGroupDTOSchema,
      },
    }),

    getLookGroupLooks: builder.query<Array<LookGroupLookDTOType>, number>({
      query: id => ({
        url: `/stylist/look-groups/${id}/looks/`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'LookGroup', id }],
      extraOptions: {
        zodSchema: LookGroupLookDTOSchema.array(),
      },
    }),

    updateLookGroup: builder.mutation<
      LookGroupDTOType,
      {
        id: number;
        data: {
          is_pinned?: boolean;
          primary_look_id?: number | null;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/stylist/look-groups/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'LookGroup', id }, 'LookGroup'],
    }),

    moveLookGroup: builder.mutation<
      LookGroupDTOType,
      {
        id: number;
        data: {
          before_id?: number | null;
          after_id?: number | null;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/stylist/look-groups/${id}/move/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'LookGroup', id }, 'LookGroup'],
    }),

    moveLookInGroup: builder.mutation<
      LookGroupDTOType,
      {
        id: number;
        lookId: number;
        data: {
          before_id?: number | null;
          after_id?: number | null;
        };
      }
    >({
      query: ({ id, lookId, data }) => ({
        url: `/stylist/look-groups/${id}/looks/${lookId}/move/`,
        method: 'POST',
        body: data,
      }),
      extraOptions: {
        zodSchema: LookGroupDTOSchema,
      },
    }),
  }),
});

type LooksInfiniteDraft = {
  pages?: Array<LookListResponseType | Array<LookDTOType>>;
};

const updateLookInLooksCache = (
  dispatch: (action: unknown) => unknown,
  id: number,
  updatedLook: LookDTOType,
) => {
  dispatch(
    stylistLooksApi.util.updateQueryData(
      'getLooks',
      undefined as never,
      (draft: LooksInfiniteDraft) => {
        if (!draft.pages) {
          return;
        }

        draft.pages.forEach(page => {
          if (!page) return;

          if (Array.isArray(page)) {
            const lookIndex = page.findIndex(look => look.id === id);
            if (lookIndex !== -1) {
              page[lookIndex] = updatedLook;
            }
            return;
          }

          const lookIndex = page.results.findIndex(look => look.id === id);
          if (lookIndex !== -1) {
            page.results[lookIndex] = updatedLook;
          }
        });
      },
    ),
  );
};

const updateLookByIdCache = (
  dispatch: (action: unknown) => unknown,
  id: number,
  updatedLook: LookDTOType,
) => {
  dispatch(stylistLooksApi.util.upsertQueryData('getLookById', id, updatedLook));
};

const updateLookInRelatedCaches = (
  dispatch: (action: unknown) => unknown,
  id: number,
  updatedLook: LookDTOType,
) => {
  updateLookByIdCache(dispatch, id, updatedLook);
  updateLookInLooksCache(dispatch, id, updatedLook);
};

export const {
  useGetLooksInfiniteQuery,
  useGetLookByIdQuery,
  useLazyGetLookByIdQuery,
  useRejectLookMutation,
  useUpdateLookContentMutation,
  usePublishLookMutation,
  useUnpublishLookMutation,
  useGenerateVisualSearchMutation,
  useReRunLookMutation,
  useGetLookGroupsInfiniteQuery,
  useGetLookGroupsListQuery,
  useGetLookGroupByIdQuery,
  useGetLookGroupLooksQuery,
  useUpdateLookGroupMutation,
  useMoveLookGroupMutation,
  useMoveLookInGroupMutation,
} = stylistLooksApi;
